/**
 * TradingSummaryTile
 * -------------------
 * Quick totals strip shown at the top of the master-admin Trading tab,
 * above the offers/enquiries/agents/applications sub-tabs. Self-contained:
 * fetches offers itself, receives enquiries/agents/applicationStats as
 * props (already loaded by AdminDashboard.jsx) to avoid duplicate fetches.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { HandCoins, MessageSquare, UserCircle, Users, Loader2 } from "lucide-react";

const API = getApiUrl();

function formatAmount(amount) {
  if (!amount) return "£0";
  return `£${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function StatCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div
      className="bg-white border border-border p-4 flex items-start gap-3"
      data-testid={testId}
    >
      <div className={`p-2 rounded-none ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-2xl font-bold leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub ? <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p> : null}
      </div>
    </div>
  );
}

export default function TradingSummaryTile({ enquiries = [], agents = [], applicationStats = { pending: 0 } }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/admin/offers`, { withCredentials: true })
      .then((res) => {
        if (mounted) setOffers(res.data?.offers || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const openOffers = offers.filter((o) => (o.status || "sent") === "sent");
  const acceptedOffers = offers.filter((o) => o.status === "accepted");
  const acceptedTotal = acceptedOffers.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4"
      data-testid="trading-summary-tile"
    >
      {loading ? (
        <div className="col-span-full flex items-center justify-center py-6 bg-white border border-border">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <StatCard
            icon={HandCoins}
            label="Open offers"
            value={openOffers.length}
            sub={`${offers.length} total`}
            accent="bg-amber-50 text-amber-600"
            testId="trading-summary-open-offers"
          />
          <StatCard
            icon={HandCoins}
            label="Accepted value"
            value={formatAmount(acceptedTotal)}
            sub={`${acceptedOffers.length} accepted offer${acceptedOffers.length === 1 ? "" : "s"}`}
            accent="bg-green-50 text-green-600"
            testId="trading-summary-accepted-value"
          />
          <StatCard
            icon={MessageSquare}
            label="Enquiries"
            value={enquiries.length}
            accent="bg-blue-50 text-blue-600"
            testId="trading-summary-enquiries"
          />
          <StatCard
            icon={UserCircle}
            label="Agents"
            value={agents.length}
            accent="bg-slate-50 text-slate-600"
            testId="trading-summary-agents"
          />
          <StatCard
            icon={Users}
            label="Pending applications"
            value={applicationStats.pending || 0}
            accent="bg-red-50 text-red-600"
            testId="trading-summary-pending-applications"
          />
        </>
      )}
    </div>
  );
}
